import { useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

export interface UseSelectedOtcOrderResult<T> {
  order: T | null;
  /** Resolving an id that is not on the loaded page — the caller shows a skeleton, not an empty drawer. */
  isLoading: boolean;
  /** The id does not exist for this caller: deleted, another organization's, or simply wrong. */
  notFound: boolean;
}

interface Snapshot<T> {
  id: string;
  order: T | null;
  notFound: boolean;
}

/**
 * Resolves the record the URL points at. The loaded page wins when it has the row (it is the polled, live
 * copy); a deep link to a row outside it is fetched by id. `fetchOrder` is a dependency: pass a module-level
 * service function.
 */
export function useSelectedOtcOrder<T extends { id: string }>(
  orderId: string | null,
  rows: T[],
  fetchOrder: (orderId: string) => Promise<T>,
): UseSelectedOtcOrderResult<T> {
  const [snapshot, setSnapshot] = useState<Snapshot<T> | null>(null);

  const fromPage = orderId ? (rows.find((row) => row.id === orderId) ?? null) : null;
  const current = snapshot?.id === orderId ? snapshot : null;
  const needsFetch = orderId !== null && fromPage === null;

  useEffect(() => {
    if (!orderId || !needsFetch) return;
    let cancelled = false;
    fetchOrder(orderId)
      .then((order) => {
        if (!cancelled) setSnapshot({ id: orderId, order, notFound: false });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setSnapshot({ id: orderId, order: null, notFound: HttpApiService.isNotFoundError(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, needsFetch, fetchOrder]);

  return {
    order: fromPage ?? current?.order ?? null,
    isLoading: needsFetch && current === null,
    notFound: fromPage === null && (current?.notFound ?? false),
  };
}
